import React, { useState, useEffect } from "react";
import Overridable from "react-overridable";
import { Container, Button, Message } from "semantic-ui-react";
import { useLocation } from "react-router-dom";
import DashboardHeader from "./DashboardHeader";
import DashboardAdvisory from "./DashboardDetails/DashboardAdvisory";
import DashboardCalendar from "./DashboardDetails/DashboardCalendar";
import EventModalsWrapper from "./DashboardDetails/modals/EventModalsWrapper";
import { sessionManager } from "@authentication/services/SessionManager";
import { calendarApi } from "@api/calendar";
import { FeedbackService } from "@api/feedback/feedback";
import Feedback from "@pages/frontsite/Feedback/Feedback";
import FeedbackContext from "@pages/frontsite/Feedback/FeedbackContext";
import WelcomeModal from "@components/GuidedTour/WelcomeModal";

const Dashboard = () => {
  const location = useLocation(); // Add this hook
  const [data, setData] = useState({ events: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modals, setModals] = useState({
    info: false,
    add: false,
    edit: false,
    delete: false,
    post: false,
  });
  const [forms, setForms] = useState({
    event: { title: "", start: "", end: "", description: "", color: "#8b5cf6" },
  });
  const [selected, setSelected] = useState(null);
  const [activeTab, setActiveTab] = useState("advisory");
  const [feedbackCount, setFeedbackCount] = useState(0);

  const user = sessionManager.user || {};
  const roles = Array.isArray(user.roles)
    ? user.roles.map((r) => r.toLowerCase())
    : [user.roles?.toLowerCase()];
  const isAllowedToEdit =
    roles.includes("admin") || roles.includes("librarian");

  const [showWelcomeModal, setShowWelcomeModal] = useState(true);

  // Add useEffect to handle URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tab = urlParams.get("tab");
    if (tab && ["advisory", "calendar", "feedback"].includes(tab)) {
      setActiveTab(tab);
    }
  }, [location]);

  useEffect(() => {
    const loadEvents = async () => {
      try {
        setLoading(true);
        setError(null);
        if (calendarApi.testBackendConnection) {
          const connectionTest = await calendarApi.testBackendConnection();
          if (!connectionTest.success)
            throw new Error(
              `Backend connection failed: ${connectionTest.error}`
            );
        }
        const response = await calendarApi.getCurrentAcademicYearEvents();
        const rawEvents = response.data.hits || response.data.data || [];
        const formattedEvents = rawEvents.map((event) => ({
          id: event.id,
          title: event.title,
          description: event.description,
          start: event.start,
          end: event.end,
          color: event.color,
          event_type: event.event_type || "general",
          academic_year: event.academic_year || "2025-2026",
        }));
        const validEvents = formattedEvents.filter((event) => {
          if (!event.start || !event.end)
            console.warn("⚠️ Event missing start/end dates:", event);
          return event.start && event.end;
        });
        setData({ events: validEvents });
      } catch (error) {
        setError(`Failed to load calendar events: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };
    loadEvents();
  }, []);

  useEffect(() => {
    const loadFeedbackCount = async () => {
      try {
        const feedbacks = await FeedbackService.getFeedbackList();
        setFeedbackCount(Array.isArray(feedbacks) ? feedbacks.length : 0);
      } catch (error) {
        setFeedbackCount(0);
      }
    };
    loadFeedbackCount();
  }, []);

  const toggleModal = (name) => setModals((m) => ({ ...m, [name]: !m[name] }));
  const updateForm = (type, field, value) =>
    setForms((f) => ({ ...f, [type]: { ...f[type], [field]: value } }));
  const handleTabChange = (tab) => setActiveTab(tab);
  const handleEventClick = (event) => {
    setSelected(event);
    toggleModal("info");
  };
  const handleAddEvent = () => {
    if (!isAllowedToEdit)
      return alert("You do not have permission to add events.");
    setForms({
      event: {
        title: "",
        start: "",
        end: "",
        description: "",
        color: "#8b5cf6",
      },
    });
    toggleModal("add");
  };
  const handleEditEvent = (event) => {
    if (!isAllowedToEdit)
      return alert("You do not have permission to edit events.");
    setSelected(event);
    setForms({
      event: {
        title: event.title || "",
        start: event.start ? event.start.split("T")[0] : "",
        end: event.end ? event.end.split("T")[0] : "",
        description: event.description || "",
        color: event.color || "#8b5cf6",
      },
    });
    toggleModal("edit");
  };
  const handleDeleteEvent = (event) => {
    if (!isAllowedToEdit)
      return alert("You do not have permission to delete events.");
    setSelected(event);
    toggleModal("delete");
  };

  const submitEvent = async (action) => {
    if (!isAllowedToEdit)
      return alert("You do not have permission to add or edit events.");
    if (!forms.event.title || !forms.event.start || !forms.event.end)
      return alert(
        "Please fill in all required fields (Title, Start Date, End Date)."
      );
    if (new Date(forms.event.start) > new Date(forms.event.end))
      return alert("End date must be after or equal to start date.");
    try {
      if (action === "add") {
        const eventData = {
          ...forms.event,
          event_type: "general",
          academic_year: "2025-2026",
        };
        const response = await calendarApi.create(eventData);
        setData((prev) => ({
          ...prev,
          events: [...prev.events, response.data],
        }));
        toggleModal("add");
        setForms({
          event: {
            title: "",
            start: "",
            end: "",
            description: "",
            color: "#8b5cf6",
          },
        });
      } else if (action === "edit" && selected) {
        const eventData = {
          ...forms.event,
          event_type: selected.event_type || "general",
          academic_year: selected.academic_year || "2025-2026",
        };
        const response = await calendarApi.update(selected.id, eventData);
        setData((prev) => ({
          ...prev,
          events: prev.events.map((e) =>
            e.id === selected.id ? response.data : e
          ),
        }));
        toggleModal("edit");
      }
      setSelected(null);
    } catch (error) {
      alert(`Error saving event: ${error.message}`);
    }
  };

  const deleteEvent = async () => {
    if (!isAllowedToEdit || !selected)
      return alert("You do not have permission to delete events.");
    try {
      await calendarApi.delete(selected.id);
      setData((prev) => ({
        ...prev,
        events: prev.events.filter((e) => e.id !== selected.id),
      }));
      toggleModal("delete");
      setSelected(null);
    } catch (error) {
      alert("Error deleting event. Please try again.");
    }
  };

  if (loading)
    return (
      <Overridable id="Dashboard.loading">
        <Container
          fluid
          style={{ width: "100%", padding: 0, maxHeight: "100%" }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "400px",
              backgroundImage: `linear-gradient(to bottom, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.8)), url(${process.env.PUBLIC_URL}/images/conference.jpg)`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              backgroundAttachment: "fixed",
            }}
          >
            <Message info>
              <Message.Header>Loading Calendar Events...</Message.Header>
              <p>Please wait while we fetch the latest events.</p>
            </Message>
          </div>
        </Container>
      </Overridable>
    );
  if (error)
    return (
      <Overridable id="Dashboard.error">
        <Container
          fluid
          style={{ width: "100%", padding: 0, maxHeight: "100%" }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "400px",
              backgroundImage: `linear-gradient(to bottom, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.8)), url(${process.env.PUBLIC_URL}/images/conference.jpg)`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              backgroundAttachment: "fixed",
            }}
          >
            <Message error>
              <Message.Header>Error Loading Calendar Events</Message.Header>
              <p>{error}</p>
              <Button color="red" onClick={() => window.location.reload()}>
                Retry
              </Button>
            </Message>
          </div>
        </Container>
      </Overridable>
    );

  return (
    <FeedbackContext.Provider value={{ feedbackCount, setFeedbackCount }}>
      <Overridable id="Dashboard.layout">
        <Container
          fluid
          style={{ width: "100%", padding: 0, maxHeight: "100%" }}
        >
          <DashboardHeader
            dashboardHeaderHeight="60px"
            isAllowedToEdit={isAllowedToEdit}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            user={user}
          />
          <div
            style={{
              backgroundImage: `linear-gradient(to bottom, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.8)), url(${process.env.PUBLIC_URL}/images/conference.jpg)`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              backgroundAttachment: "fixed",
              maxHeight: "100%",
              width: "100%",
              padding: "2em 1em",
            }}
          >
            {activeTab === "advisory" && (
              <DashboardAdvisory
                canEdit={isAllowedToEdit}
                modalOpen={modals.post}
                onToggleModal={() => toggleModal("post")}
              />
            )}
            {activeTab === "calendar" && (
              <div data-tour="calendar-content">
                <DashboardCalendar
                  events={data.events}
                  canEdit={isAllowedToEdit}
                  onEventClick={handleEventClick}
                  onAddEvent={handleAddEvent}
                  data-tour="calendar-events"
                />
              </div>
            )}
            {activeTab === "feedback" && <Feedback />}
          </div>
          <EventModalsWrapper
            modals={modals}
            toggleModal={toggleModal}
            forms={forms}
            updateForm={updateForm}
            selected={selected}
            submitEvent={submitEvent}
            deleteEvent={deleteEvent}
            setSelected={setSelected}
            canEdit={isAllowedToEdit}
            onEdit={handleEditEvent}
            onDelete={handleDeleteEvent}
          />
          <WelcomeModal
            user={user}
            isAuthenticated
            setShowWelcomeModal={setShowWelcomeModal}
            showWelcomeModal={showWelcomeModal}
          />
        </Container>
      </Overridable>
    </FeedbackContext.Provider>
  );
};

export default Overridable.component("Dashboard", Dashboard);
